import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import { useAiInsightsStartupJobs } from "../../api/aiInsights";
import { isApiError } from "../../api/errors";
import {
  MAPPING_READINESS_POLL_INTERVAL_MS,
  useMappingReadiness,
} from "../../api/mappingReadiness";
import { useHasPlatformRole } from "../../api/userAccess";
import { AppRoutes } from "../../types/enums/appRoutes";
import { PLANNING_VIEW_ALLOWED_ROLES } from "../../types/enums/roleKeys";

const STARTUP_TOAST_ID = "ai-insights-startup";

function isInsightsStartupRoute(pathname: string): boolean {
  return pathname.startsWith(AppRoutes.checklist)
    || pathname.startsWith(AppRoutes.decisionsLog)
    || pathname.startsWith(AppRoutes.raid)
    || pathname.startsWith(AppRoutes.integrationPlanning);
}

function isExpectedAvailabilityError(error: unknown): boolean {
  return isApiError(error) &&
    (error.code === "no_data"
      || error.code === "no_persona"
      || error.code === "mapping_required"
      || error.code === "document_processing");
}

/**
 * Starts every durable insight job once after an authenticated session has a
 * readiness-eligible deal-data version. Individual insight shelves use the same React Query entries,
 * so opening one while this is running retains its existing loading state.
 */
export function AiInsightsStartup() {
  const { pathname } = useLocation();
  const canViewPlanning = useHasPlatformRole(PLANNING_VIEW_ALLOWED_ROLES);
  const startupEnabled = canViewPlanning && isInsightsStartupRoute(pathname);
  const mappingReadinessQuery = useMappingReadiness(
    startupEnabled,
    MAPPING_READINESS_POLL_INTERVAL_MS
  );
  const dealDataVersionId = mappingReadinessQuery.data?.dealDataVersionId ?? null;
  const generationKey = dealDataVersionId == null ? null : `deal-data-version:${dealDataVersionId}`;
  const canStartInsights = startupEnabled
    && generationKey != null
    && mappingReadinessQuery.isSuccess
    && mappingReadinessQuery.data.mappingComplete
    && mappingReadinessQuery.data.insights.eligible === true
    && mappingReadinessQuery.data.dealDataVersionId != null;
  const jobs = useAiInsightsStartupJobs(canStartInsights);
  // Track by immutable DealDataVersion id so a mapping change that produces a
  // new version during the same mounted session is recognised as a fresh
  // generation to start and confirm without a reload.
  const startedKey = useRef<string | null>(null);
  const completedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!startupEnabled) {
      toast.dismiss(STARTUP_TOAST_ID);
    }
  }, [startupEnabled]);

  useEffect(() => {
    if (!canStartInsights || generationKey == null) return;
    if (startedKey.current === generationKey) return;

    startedKey.current = generationKey;
    toast.loading("Generating insights…", { id: STARTUP_TOAST_ID, duration: Infinity });
  }, [canStartInsights, generationKey]);

  useEffect(() => {
    if (!canStartInsights || generationKey == null) return;
    if (startedKey.current !== generationKey || completedKey.current === generationKey) return;
    if (!jobs.every((job) => job.isSuccess || job.isError)) return;

    completedKey.current = generationKey;
    const errors = jobs.filter((job) => job.isError).map((job) => job.error);

    if (errors.length === 0) {
      toast.success("Insights successfully generated.", { id: STARTUP_TOAST_ID, duration: 4_000 });
      return;
    }

    if (errors.every(isExpectedAvailabilityError)) {
      toast.dismiss(STARTUP_TOAST_ID);
      return;
    }

    toast.error("Some insights could not be generated.", { id: STARTUP_TOAST_ID });
  }, [canStartInsights, generationKey, jobs]);

  return null;
}
